import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const WishlistContext = createContext(null);
export const useWishlist = () => useContext(WishlistContext);

const STORAGE_KEY = "sbr_wishlist";

const readLocal = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
};

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => readLocal());
  const syncedForUser = useRef(null);

  // Persist locally on every change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  }, [ids]);

  // On login: merge local ⇢ server, then load server truth. Run once per user session.
  useEffect(() => {
    if (!user) {
      syncedForUser.current = null;
      return;
    }
    if (syncedForUser.current === user.user_id) return;
    syncedForUser.current = user.user_id;

    (async () => {
      try {
        const { data: serverProducts } = await api.get("/wishlist");
        const serverIds = new Set(serverProducts.map((p) => p.product_id));
        const local = readLocal();
        const toPush = local.filter((id) => !serverIds.has(id));
        for (const pid of toPush) {
          try {
            await api.post(`/wishlist/${pid}`);
            serverIds.add(pid);
          } catch (_) {}
        }
        setIds([...serverIds]);
      } catch (_) {
        // stay with local
      }
    })();
  }, [user]);

  const has = useCallback((product_id) => ids.includes(product_id), [ids]);

  const toggle = useCallback(
    async (product_id, productName) => {
      const willAdd = !ids.includes(product_id);
      // Optimistic local update
      setIds((prev) => (willAdd ? [...new Set([...prev, product_id])] : prev.filter((x) => x !== product_id)));
      if (willAdd) {
        toast.success("Saved to wishlist", { description: productName });
      } else {
        toast.message("Removed from wishlist", { description: productName });
      }
      if (user) {
        try {
          await api.post(`/wishlist/${product_id}`);
        } catch (_) {
          // ignore - local state still updated
        }
      }
    },
    [ids, user]
  );

  return (
    <WishlistContext.Provider value={{ ids, has, toggle, count: ids.length }}>
      {children}
    </WishlistContext.Provider>
  );
}
